import * as yup from "yup";
import { useFormik } from "formik";
import { useHistory, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useSubdomain } from "./SubdomainProvider.js";
import ResponsiveSelect from "./ResponsiveSelect.js";
import { callMakeWebhook } from "../config/makeWebhooks.js";

const ACT_OPTIONS_APP = [
  "Act 1 (Mission Start)",
  "Act 2 (Papyrus Call)",
  "Act 4 (Hashimoto)",
  "Act 5 (Marble Search)",
  "Act 6 (Friendship Knot)",
  "Act 7 (Obelisk)",
  "Act 7 (Sweat Yoga)",
  "Act 8 (Marble)",
  "Act 10 (Papyrus)",
];

const ACT_OPTIONS_SEATTLE = [
  "Act 1 (Mission Start)",
  "Act 2 (Lonely Siren)",
  "Act 3 (Marble Puzzle)",
  "Act 4 (Selfie Request / Marble Search)",
  "Act 5 (Gum Wall)",
  "Act 6 (Cafe Campagne / Marble Selfie)",
  "Act 7 (Inn at the Market)",
  "Act 8 (Marble)",
  "Act 10 (Papyrus)",
];

/** Map any current-act string/code onto a valid Bypass act option for this site. */
function resolveActOption(act, actOptions) {
  const fallback = actOptions[0] || "Act 1 (Mission Start)";
  if (!act) return fallback;

  const actStr = String(act).trim();
  if (actOptions.includes(actStr)) return actStr;

  // Prefer leading "Act #" (e.g. "Act 6.5 (Marble Selfie) Path 1" → 6)
  const actPrefixMatch = actStr.match(/^Act\s+(\d+)/i);
  const codeMatch = actStr.match(/^(\d+)/);
  const actNumber = actPrefixMatch?.[1] || codeMatch?.[1];
  if (!actNumber) return fallback;

  const candidates = actOptions.filter((opt) => {
    const optMatch = opt.match(/^Act\s+(\d+)/i);
    return optMatch && optMatch[1] === actNumber;
  });
  if (candidates.length === 0) return fallback;
  if (candidates.length === 1) return candidates[0];

  // Disambiguate (e.g. app Act 7 Obelisk vs Sweat Yoga) via keywords / short codes
  const lower = actStr.toLowerCase();
  let best = candidates[0];
  let bestScore = -1;
  for (const candidate of candidates) {
    const words =
      candidate
        .replace(/^Act\s+\d+\s*/i, "")
        .toLowerCase()
        .match(/[a-z]+/g) || [];
    let score = words.filter((w) => w.length > 2 && lower.includes(w)).length;
    if (/^7a$/i.test(actStr) && /obelisk/i.test(candidate)) score += 10;
    if (/^7h$/i.test(actStr) && /sweat|yoga/i.test(candidate)) score += 10;
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best;
}

function Bypass() {
  const history = useHistory();
  const location = useLocation();
  const prepopulatedData = location.state?.prepopulatedData || null;
  const subdomain = useSubdomain();
  const actOptions =
    subdomain === "seattle" ? ACT_OPTIONS_SEATTLE : ACT_OPTIONS_APP;
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const [specialStatusForMake, setSpecialStatusForMake] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/settings`)
      .then((res) => (res.ok ? res.json() : Promise.resolve({})))
      .then((data) => {
        const key =
          subdomain === "seattle" ? "specialEventSeattle" : "specialEventApp";
        const siteFlag =
          typeof data[key] === "boolean"
            ? data[key]
            : data.specialEvent === true;
        setSpecialStatusForMake(siteFlag === true);
      })
      .catch(() => {
        setSpecialStatusForMake(false);
      });
  }, [API_URL, subdomain]);

  const formSchema = yup.object().shape({
    firstName: yup.string().required("Enter a first name"),
    lastName: yup.string().required("Enter a last name"),
    phone1: yup
      .string()
      .required("Enter a 10 digit phone number")
      .matches(/^[0-9]{10}$/, "Enter a 10 digit phone number")
      .test(
        "unique-phone",
        "Two players can not use the same phone number",
        function (value) {
          const { phone2, phone3, phone4, phone5, phone6 } = this.parent;
          if (!value) return true;
          return (
            value !== phone2 &&
            value !== phone3 &&
            value !== phone4 &&
            value !== phone5 &&
            value !== phone6
          );
        }
      ),
    name2: yup
      .string()
      .test(
        "required-player2-name",
        "Enter a name for Player 2",
        function (value) {
          const { numberofplayers } = this.parent;
          return parseInt(numberofplayers || "0") < 2 || !!value;
        }
      ),
    phone2: yup
      .string()
      .test(
        "required-player2-phone",
        "Enter a 10 digit phone number",
        function (value) {
          const { numberofplayers } = this.parent;
          return (
            parseInt(numberofplayers || "0") < 2 ||
            (value && /^[0-9]{10}$/.test(value))
          );
        }
      )
      .test(
        "unique-phone",
        "Two players can not use the same phone number",
        function (value) {
          const { numberofplayers, phone1, phone3, phone4, phone5, phone6 } =
            this.parent;
          if (parseInt(numberofplayers || "0") < 2 || !value) return true;
          return (
            value !== phone1 &&
            value !== phone3 &&
            value !== phone4 &&
            value !== phone5 &&
            value !== phone6
          );
        }
      ),
    name3: yup
      .string()
      .test(
        "required-player3-name",
        "Enter a name for Player 3",
        function (value) {
          const { numberofplayers } = this.parent;
          return parseInt(numberofplayers || "0") < 3 || !!value;
        }
      ),
    phone3: yup
      .string()
      .test(
        "required-player3-phone",
        "Enter a 10 digit phone number",
        function (value) {
          const { numberofplayers } = this.parent;
          return (
            parseInt(numberofplayers || "0") < 3 ||
            (value && /^[0-9]{10}$/.test(value))
          );
        }
      )
      .test(
        "unique-phone",
        "Two players can not use the same phone number",
        function (value) {
          const { numberofplayers, phone1, phone2, phone4, phone5, phone6 } =
            this.parent;
          if (parseInt(numberofplayers || "0") < 3 || !value) return true;
          return (
            value !== phone1 &&
            value !== phone2 &&
            value !== phone4 &&
            value !== phone5 &&
            value !== phone6
          );
        }
      ),
    name4: yup
      .string()
      .test(
        "required-player4-name",
        "Enter a name for Player 4",
        function (value) {
          const { numberofplayers } = this.parent;
          return parseInt(numberofplayers || "0") < 4 || !!value;
        }
      ),
    phone4: yup
      .string()
      .test(
        "required-player4-phone",
        "Enter a 10 digit phone number",
        function (value) {
          const { numberofplayers } = this.parent;
          return (
            parseInt(numberofplayers || "0") < 4 ||
            (value && /^[0-9]{10}$/.test(value))
          );
        }
      )
      .test(
        "unique-phone",
        "Two players can not use the same phone number",
        function (value) {
          const { numberofplayers, phone1, phone2, phone3, phone5, phone6 } =
            this.parent;
          if (parseInt(numberofplayers || "0") < 4 || !value) return true;
          return (
            value !== phone1 &&
            value !== phone2 &&
            value !== phone3 &&
            value !== phone5 &&
            value !== phone6
          );
        }
      ),
    name5: yup
      .string()
      .test(
        "required-player5-name",
        "Enter a name for Player 5",
        function (value) {
          const { numberofplayers } = this.parent;
          return parseInt(numberofplayers || "0") < 5 || !!value;
        }
      ),
    phone5: yup
      .string()
      .test(
        "required-player5-phone",
        "Enter a 10 digit phone number",
        function (value) {
          const { numberofplayers } = this.parent;
          return (
            parseInt(numberofplayers || "0") < 5 ||
            (value && /^[0-9]{10}$/.test(value))
          );
        }
      )
      .test(
        "unique-phone",
        "Two players can not use the same phone number",
        function (value) {
          const { numberofplayers, phone1, phone2, phone3, phone4, phone6 } =
            this.parent;
          if (parseInt(numberofplayers || "0") < 5 || !value) return true;
          return (
            value !== phone1 &&
            value !== phone2 &&
            value !== phone3 &&
            value !== phone4 &&
            value !== phone6
          );
        }
      ),
    name6: yup
      .string()
      .test(
        "required-player6-name",
        "Enter a name for Player 6",
        function (value) {
          const { numberofplayers } = this.parent;
          return parseInt(numberofplayers || "0") < 6 || !!value;
        }
      ),
    phone6: yup
      .string()
      .test(
        "required-player6-phone",
        "Enter a 10 digit phone number",
        function (value) {
          const { numberofplayers } = this.parent;
          return (
            parseInt(numberofplayers || "0") < 6 ||
            (value && /^[0-9]{10}$/.test(value))
          );
        }
      )
      .test(
        "unique-phone",
        "Two players can not use the same phone number",
        function (value) {
          const { numberofplayers, phone1, phone2, phone3, phone4, phone5 } =
            this.parent;
          if (parseInt(numberofplayers || "0") < 6 || !value) return true;
          return (
            value !== phone1 &&
            value !== phone2 &&
            value !== phone3 &&
            value !== phone4 &&
            value !== phone5
          );
        }
      ),
    numberofplayers: yup.string().required("Number of Agents is required"),
    nostairs: yup.boolean(),
    agreeToTerms: yup
      .boolean()
      .oneOf([true], "Agree to the terms and conditions to proceed"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: prepopulatedData?.firstName || "",
      lastName: prepopulatedData?.lastName || "",
      name1: prepopulatedData?.firstName && prepopulatedData?.lastName 
        ? `${prepopulatedData.firstName} ${prepopulatedData.lastName}`.trim()
        : "",
      phone1: prepopulatedData?.phone1 || "",
      name2: prepopulatedData?.name2 || "",
      phone2: prepopulatedData?.phone2 || "",
      name3: prepopulatedData?.name3 || "",
      phone3: prepopulatedData?.phone3 || "",
      name4: prepopulatedData?.name4 || "",
      phone4: prepopulatedData?.phone4 || "",
      name5: prepopulatedData?.name5 || "",
      phone5: prepopulatedData?.phone5 || "",
      name6: prepopulatedData?.name6 || "",
      phone6: prepopulatedData?.phone6 || "",
      numberofplayers: prepopulatedData?.numberofplayers || "1",
      act: resolveActOption(prepopulatedData?.act, actOptions),
      nostairs: prepopulatedData?.nostairs || false,
      agreeToTerms: prepopulatedData?.agreeToTerms || false,
      waittime: "0",
    },
    validationSchema: formSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: (values) => {
      console.log("Submitted values:", values);

      // Transform form data to match API format
      const formatPhone = (phone) => {
        if (!phone) return "";
        const digits = phone.replace(/\D/g, "");
        return digits.length === 10 ? `+1${digits}` : phone;
      };

      const apiData = {
        player1_name:
          values.name1 || `${values.firstName} ${values.lastName}`.trim(),
        player1_phone: formatPhone(values.phone1),
        player2_name: values.name2 || "",
        player2_phone: formatPhone(values.phone2),
        player3_name: values.name3 || "",
        player3_phone: formatPhone(values.phone3),
        player4_name: values.name4 || "",
        player4_phone: formatPhone(values.phone4),
        player5_name: values.name5 || "",
        player5_phone: formatPhone(values.phone5),
        player6_name: values.name6 || "",
        player6_phone: formatPhone(values.phone6),
        number_of_players: values.numberofplayers,
        current_act: values.act || "Act 1 (Mission Start)",
        nostairs: values.nostairs || false,
        texts: [],
        subdomain: subdomain,
      };

      const AUTH_TOKEN = process.env.REACT_APP_AUTH_TOKEN || "";

      // Call make webhook first (return promise so Formik keeps isSubmitting true until done)
      return callMakeWebhook("begin", subdomain, {
        data: {
          ...values,
          specialstatus: specialStatusForMake,
        },
      })
        .then((makeResponse) => {
          // Check if make webhook returned 200
          if (makeResponse.status === 200) {
            console.log("Make webhook successful:", makeResponse);
            
            // Only call API webhook if make webhook returned 200
            return fetch(`${API_URL}/api/webhook/player-progress`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(AUTH_TOKEN && { Authorization: `Bearer ${AUTH_TOKEN}` }),
              },
              body: JSON.stringify(apiData),
            })
              .then((apiResponse) => {
                return apiResponse.json().then((data) => {
                  console.log("API webhook response:", data);
                  return { makeResponse, apiResponse: data };
                });
              })
              .catch((err) => {
                console.warn("API webhook error (non-blocking):", err);
                return { makeResponse, apiResponse: null };
              });
          } else {
            // Make webhook did not return 200, don't call API webhook
            throw new Error(
              `Make webhook returned status ${makeResponse.status} - A mission with this phone number is already in progress, please cancel that mission before trying to create a new one.`
            );
          }
        })
        .then((results) => {
          // Success: make webhook returned 200 (and API webhook was called)
          console.log("successful response", results.makeResponse);
          history.push("/confirmed#top");
        })
        .catch((error) => {
          // Error: make webhook failed or returned non-200
          const errorMsg = error.message || "Unknown error";
          console.error("Make webhook error:", errorMsg);
          alert(`Error: ${errorMsg}`);
        });
    },
  });
  console.log("Form Values:", formik.values);

  useEffect(() => {
    const { firstName, lastName } = formik.values;
    if (firstName || lastName) {
      formik.setFieldValue("name1", `${firstName} ${lastName}`.trim());
    }
    // eslint-disable-next-line
  }, [formik.values.firstName, formik.values.lastName]);

  useEffect(() => {
    const num = parseInt(formik.values.numberofplayers || "1");

    const fieldsToClear = [];
    if (num < 6) {
      fieldsToClear.push("name6", "phone6");
    }
    if (num < 5) {
      fieldsToClear.push("name5", "phone5");
    }
    if (num < 4) {
      fieldsToClear.push("name4", "phone4");
    }
    if (num < 3) {
      fieldsToClear.push("name3", "phone3");
    }
    if (num < 2) {
      fieldsToClear.push("name2", "phone2");
    }

    // Only reset fields that have values
    fieldsToClear.forEach((field) => {
      if (formik.values[field] !== "") {
        formik.setFieldValue(field, "");
      }
    });
    // eslint-disable-next-line
  }, [formik.values.numberofplayers]);

  const phoneInputRefs = useRef({});

  const formatPhone = (value) => {
    // Extract only digits
    const digits = (value || "").replace(/\D/g, "");
    // Add dashes after 3rd and 6th digits
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }
  };

  const handlePhoneChange = (fieldName) => (e) => {
    const input = e.target;
    const inputValue = input.value;
    const cursorPos = input.selectionStart;

    // Extract only digits and limit to 10
    const digitsOnly = inputValue.replace(/\D/g, "").slice(0, 10);

    // Count digits in the current input value up to cursor position
    // This represents where the cursor is in the current (browser-modified) input
    const digitsBeforeCursorInInput = inputValue
      .slice(0, cursorPos)
      .replace(/\D/g, "").length;

    // Update the value
    formik.setFieldValue(fieldName, digitsOnly);

    // Restore cursor position after React re-renders
    // Use double requestAnimationFrame to ensure React has fully updated the DOM
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const inputElement = phoneInputRefs.current[fieldName] || input;
        if (inputElement) {
          const newFormatted = formatPhone(digitsOnly);

          // The target digit count is based on where the cursor was in the input
          // after the browser's modification
          const targetDigitCount = digitsBeforeCursorInInput;

          // Find position in formatted string with target number of digits before it
          let newCursorPos;
          
          if (targetDigitCount === 0) {
            newCursorPos = 0;
          } else if (targetDigitCount >= digitsOnly.length) {
            // If target is at or beyond the end, place at the end
            newCursorPos = newFormatted.length;
          } else {
            // Find the position after the targetDigitCount-th digit
            let digitCount = 0;
            for (let i = 0; i < newFormatted.length; i++) {
              if (/\d/.test(newFormatted[i])) {
                digitCount++;
                if (digitCount === targetDigitCount) {
                  newCursorPos = i + 1;
                  break;
                }
              }
            }
            // Fallback: if we didn't break (shouldn't happen), place at end
            if (newCursorPos === undefined) {
              newCursorPos = newFormatted.length;
            }
          }

          inputElement.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    });
  };

  return (
    <div className="pageContent" style={{ paddingBottom: "10vw" }}>
      <title>Bypass | Spies Among Us</title>

      <div className="orangeBar">MISSION BYPASS</div>

      <form onSubmit={formik.handleSubmit} className="startPageForm">
        <label htmlFor="firstName">First name</label>
        <br />
        <input
          type="text"
          name="firstName"
          className="formField"
          value={formik.values.firstName}
          onChange={formik.handleChange}
        />
        <br />
        <h3 style={{ color: "#ff3700" }}> {formik.errors.firstName}</h3>

        <label htmlFor="lastName">Last name</label>
        <br />
        <input
          type="text"
          name="lastName"
          className="formField"
          value={formik.values.lastName}
          onChange={formik.handleChange}
        />
        <br />
        <h3 style={{ color: "#ff3700" }}> {formik.errors.lastName}</h3>

        <label htmlFor="phone1">Phone</label>
        <br />
        <input
          type="tel"
          name="phone1"
          className="formField"
          value={formatPhone(formik.values.phone1)}
          onChange={handlePhoneChange("phone1")}
          ref={(el) => (phoneInputRefs.current.phone1 = el)}
        />
        <br />
        <h3 style={{ color: "#ff3700" }}> {formik.errors.phone1}</h3>

        <label htmlFor="numberofplayers">
          Number of agents on your mission
        </label>
        <br />
        <select
          name="numberofplayers"
          id="numberofplayers"
          className="formField"
          value={formik.values.numberofplayers}
          onChange={formik.handleChange}
        >
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="6">6</option>
        </select>
        <br />
        <br />

        {/* <span className="secondAgentText">
          Other agents will receive all the texts and clues you receive, but you will be responsible for replying to texts.
        </span>
        <br />
        <br /> */}

        {formik.values.numberofplayers >= "2" && (
          <div id="name2">
            <label htmlFor="name2">2nd Agent's Name</label>
            <br />
            <input
              type="text"
              name="name2"
              className="formField"
              value={formik.values.name2}
              onChange={formik.handleChange}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.name2}</h3>

        {formik.values.numberofplayers >= "2" && (
          <div id="phone2">
            <label htmlFor="phone2">2nd Agent's Phone Number</label>
            <br />
            <input
              type="tel"
              name="phone2"
              className="formField"
              value={formatPhone(formik.values.phone2)}
              onChange={handlePhoneChange("phone2")}
              ref={(el) => (phoneInputRefs.current.phone2 = el)}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.phone2}</h3>

        {formik.values.numberofplayers >= "3" && (
          <div id="name3">
            <label htmlFor="name3">3rd Agent's Name</label>
            <br />
            <input
              type="text"
              name="name3"
              className="formField"
              value={formik.values.name3}
              onChange={formik.handleChange}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.name3}</h3>

        {formik.values.numberofplayers >= "3" && (
          <div id="phone3">
            <label htmlFor="phone3">3rd Agent's Phone Number</label>
            <br />
            <input
              type="tel"
              name="phone3"
              className="formField"
              value={formatPhone(formik.values.phone3)}
              onChange={handlePhoneChange("phone3")}
              ref={(el) => (phoneInputRefs.current.phone3 = el)}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.phone3}</h3>

        {formik.values.numberofplayers >= "4" && (
          <div id="name4">
            <label htmlFor="name4">4th Agent's Name</label>
            <br />
            <input
              type="text"
              name="name4"
              className="formField"
              value={formik.values.name4}
              onChange={formik.handleChange}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.name4}</h3>

        {formik.values.numberofplayers >= "4" && (
          <div id="phone4">
            <label htmlFor="phone4">4th Agent's Phone Number</label>
            <br />
            <input
              type="tel"
              name="phone4"
              className="formField"
              value={formatPhone(formik.values.phone4)}
              onChange={handlePhoneChange("phone4")}
              ref={(el) => (phoneInputRefs.current.phone4 = el)}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.phone4}</h3>

        {formik.values.numberofplayers >= "5" && (
          <div id="name5">
            <label htmlFor="name5">5th Agent's Name</label>
            <br />
            <input
              type="text"
              name="name5"
              className="formField"
              value={formik.values.name5}
              onChange={formik.handleChange}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.name5}</h3>

        {formik.values.numberofplayers >= "5" && (
          <div id="phone5">
            <label htmlFor="phone5">5th Agent's Phone Number</label>
            <br />
            <input
              type="tel"
              name="phone5"
              className="formField"
              value={formatPhone(formik.values.phone5)}
              onChange={handlePhoneChange("phone5")}
              ref={(el) => (phoneInputRefs.current.phone5 = el)}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.phone5}</h3>

        {formik.values.numberofplayers >= "6" && (
          <div id="name6">
            <label htmlFor="name6">6th Agent's Name</label>
            <br />
            <input
              type="text"
              name="name6"
              className="formField"
              value={formik.values.name6}
              onChange={formik.handleChange}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.name6}</h3>

        {formik.values.numberofplayers >= "6" && (
          <div id="phone6">
            <label htmlFor="phone6">6th Agent's Phone Number</label>
            <br />
            <input
              type="tel"
              name="phone6"
              className="formField"
              value={formatPhone(formik.values.phone6)}
              onChange={handlePhoneChange("phone6")}
              ref={(el) => (phoneInputRefs.current.phone6 = el)}
            />
            <br />
          </div>
        )}
        <h3 style={{ color: "#ff3700" }}> {formik.errors.phone6}</h3>

        <label htmlFor="act">Jump to later act?</label>
        <br />
        <label htmlFor="act">(Leave at 1 if starting from beginning)</label>
        <br />
        <ResponsiveSelect
          name="act"
          id="act"
          value={formik.values.act}
          onChange={formik.handleChange}
          options={actOptions}
        />
        <br />
        <br />

        <label>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={formik.values.agreeToTerms}
              onChange={formik.handleChange}
            />
            <span className="checkbox"></span>
            <span className="checkboxLabel">
              I have read and agree to <br />
              the{" "}
              <a href="https://www.spiesamong.us/terms" target="_blank" rel="noopener noreferrer">
                terms and conditions
              </a>
              .
            </span>
          </div>
          <h3 style={{ color: "#ff3700" }}> {formik.errors.agreeToTerms}</h3>
        </label>

        <label>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <input
              type="checkbox"
              name="nostairs"
              checked={formik.values.nostairs}
              onChange={formik.handleChange}
            />
            <span className="checkbox"></span>
            <span className="checkboxLabel">
              I do not want to go up any stairs this mission.
            </span>
          </div>
          <h3 style={{ color: "#ff3700" }}> {formik.errors.nostairs}</h3>
        </label>

        <br />
        <br />
        <input
          type="submit"
          value={formik.isSubmitting ? "Submitted!" : "Begin Mission"}
          className="submitButton"
          disabled={formik.isSubmitting}
        />
      </form>
    </div>
  );
}

export default Bypass;
